import React, { useState } from "react";
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tabs,
  InputNumber,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  TagsOutlined,
  AppstoreOutlined,
  LinkOutlined,
  BgColorsOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  FetchVehicleTypes_api,
  AddVehicleType_api,
  UpdateVehicleType_api,
  FetchVehicleModals_api,
  AddVehicleModal_api,
  UpdateVehicleModal_api,
  FetchVehicleColors_api,
  AddVehicleColor_api,
  UpdateVehicleColor_api,
  FetchVTMMappings_api,
  AddVTMMapping_api,
  UpdateVTMMapping_api,
} from "../../Api/Vehicles/vehicle_api";
import { useAuthChecker } from "../../Context";

const { Option } = Select;

export const VehicleConfig = () => {
  const { user } = useAuthChecker();
  const [activeTab, setActiveTab] = useState("1");
  const [submitting, setSubmitting] = useState(false);

  // Type State
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<any>(null);
  const [typeForm] = Form.useForm();

  // Modal State
  const [modalModalOpen, setModalModalOpen] = useState(false);
  const [editingModal, setEditingModal] = useState<any>(null);
  const [modalForm] = Form.useForm();

  // Color State
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [editingColor, setEditingColor] = useState<any>(null);
  const [colorForm] = Form.useForm();

  // VTM State
  const [vtmModalOpen, setVtmModalOpen] = useState(false);
  const [editingVtm, setEditingVtm] = useState<any>(null);
  const [vtmForm] = Form.useForm();

  // Queries
  const {
    data: typesData,
    refetch: refetchTypes,
    isFetching: isFetchingTypes,
  } = useQuery({
    queryKey: ["vehicle_types"],
    queryFn: FetchVehicleTypes_api,
  });

  const {
    data: modalsData,
    refetch: refetchModals,
    isFetching: isFetchingModals,
  } = useQuery({
    queryKey: ["vehicle_modals"],
    queryFn: FetchVehicleModals_api,
  });

  const {
    data: colorsData,
    refetch: refetchColors,
    isFetching: isFetchingColors,
  } = useQuery({
    queryKey: ["vehicle_colors"],
    queryFn: FetchVehicleColors_api,
  });

  const {
    data: vtmData,
    refetch: refetchVtm,
    isFetching: isFetchingVtm,
  } = useQuery({
    queryKey: ["vtm_mappings"],
    queryFn: FetchVTMMappings_api,
  });

  // Handlers
  const handleTypeSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const payload = { ...values, id: editingType?.id, userId: user?.userId };
      const res = editingType
        ? await UpdateVehicleType_api(payload)
        : await AddVehicleType_api(payload);
      if (res.isDataAdded || res.isDataUpdated) {
        message.success(res.msg);
        setTypeModalOpen(false);
        refetchTypes();
      } else {
        message.error(res.msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const payload = { ...values, id: editingModal?.id, userId: user?.userId };
      const res = editingModal
        ? await UpdateVehicleModal_api(payload)
        : await AddVehicleModal_api(payload);
      if (res.isDataAdded || res.isDataUpdated) {
        message.success(res.msg);
        setModalModalOpen(false);
        refetchModals();
      } else {
        message.error(res.msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleColorSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const payload = { ...values, id: editingColor?.id, userId: user?.userId };
      const res = editingColor
        ? await UpdateVehicleColor_api(payload)
        : await AddVehicleColor_api(payload);
      if (res.isDataAdded || res.isDataUpdated) {
        message.success(res.msg);
        setColorModalOpen(false);
        refetchColors();
      } else {
        message.error(res.msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleVtmSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const payload = { ...values, id: editingVtm?.id, userId: user?.userId };
      const res = editingVtm
        ? await UpdateVTMMapping_api(payload)
        : await AddVTMMapping_api(payload);
      if (res.isDataAdded || res.isDataUpdated) {
        message.success(res.msg);
        setVtmModalOpen(false);
        refetchVtm();
      } else {
        message.error(res.msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const typeColumns = [
    { title: "ID", dataIndex: "id", key: "id", width: 80 },
    { title: "Type Name", dataIndex: "v_type", key: "v_type" },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (record: any) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            setEditingType(record);
            typeForm.setFieldsValue(record);
            setTypeModalOpen(true);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  const modalColumns = [
    { title: "ID", dataIndex: "id", key: "id", width: 80 },
    { title: "Model Name", dataIndex: "v_modal", key: "v_modal" },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (record: any) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            setEditingModal(record);
            modalForm.setFieldsValue(record);
            setModalModalOpen(true);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  const colorColumns = [
    { title: "ID", dataIndex: "id", key: "id", width: 80 },
    { title: "Color Name", dataIndex: "color", key: "color" },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (record: any) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            setEditingColor(record);
            colorForm.setFieldsValue(record);
            setColorModalOpen(true);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  const vtmColumns = [
    { title: "Type", dataIndex: "v_type", key: "v_type" },
    { title: "Model", dataIndex: "v_modal", key: "v_modal" },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (val: any) => `Rs. ${parseFloat(val).toLocaleString()}`,
    },
    {
      title: "Reg. Cost (lease)",
      dataIndex: "registration_cost_lease",
      key: "registration_cost_lease",
      render: (val: any) => `Rs. ${parseFloat(val).toLocaleString()}`,
    },
    {
      title: "Reg. Cost (cash)",
      dataIndex: "registration_cost_cash",
      key: "registration_cost_cash",
      render: (val: any) => `Rs. ${parseFloat(val).toLocaleString()}`,
    },
    {
      title: "Insurance",
      dataIndex: "insurance_cost",
      key: "insurance_cost",
      render: (val: any) => `Rs. ${parseFloat(val).toLocaleString()}`,
    },
    {
      title: "Transport",
      dataIndex: "transport_charges",
      key: "transport_charges",
      render: (val: any) => `Rs. ${parseFloat(val).toLocaleString()}`,
    },
    {
      title: "Special Number Level 1",
      dataIndex: "special_number_level_1",
      key: "special_number_level_1",
      render: (val: any) => `Rs. ${parseFloat(val).toLocaleString()}`,
    },
    {
      title: "Special Number Level 2",
      dataIndex: "special_number_level_2",
      key: "special_number_level_2",
      render: (val: any) => `Rs. ${parseFloat(val).toLocaleString()}`,
    },
    {
      title: "Special Number Level 3",
      dataIndex: "special_number_level_3",
      key: "special_number_level_3",
      render: (val: any) => `Rs. ${parseFloat(val).toLocaleString()}`,
    },
    {
      title: "Special Number Level 4",
      dataIndex: "special_number_level_4",
      key: "special_number_level_4",
      render: (val: any) => `Rs. ${parseFloat(val).toLocaleString()}`,
    },
    {
      title: "Special Number Level 5",
      dataIndex: "special_number_level_5",
      key: "special_number_level_5",
      render: (val: any) => `Rs. ${parseFloat(val || 0).toLocaleString()}`,
    },
    {
      title: "Rajya Pranama",
      dataIndex: "rajya_pranama",
      key: "rajya_pranama",
      render: (val: any) => `Rs. ${parseFloat(val || 0).toLocaleString()}`,
    },
    {
      title: "Age Stock",
      dataIndex: "age_stock",
      key: "age_stock",
      render: (val: any) => `Rs. ${parseFloat(val || 0).toLocaleString()}`,
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      render: (val: any) => `Rs. ${parseFloat(val).toLocaleString()}`,
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (record: any) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            setEditingVtm(record);
            vtmForm.setFieldsValue(record);
            setVtmModalOpen(true);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  const tabItems = [
    {
      key: "1",
      label: (
        <span>
          <TagsOutlined /> Types
        </span>
      ),
      children: (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Vehicle Types</h2>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingType(null);
                typeForm.resetFields();
                setTypeModalOpen(true);
              }}
            >
              Add Type
            </Button>
          </div>
          <Table
            className="overflow-x-auto"
            loading={isFetchingTypes}
            dataSource={typesData?.data || []}
            columns={typeColumns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <span>
          <AppstoreOutlined /> Models
        </span>
      ),
      children: (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Vehicle Models</h2>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingModal(null);
                modalForm.resetFields();
                setModalModalOpen(true);
              }}
            >
              Add Model
            </Button>
          </div>
          <Table
            className="overflow-x-auto"
            loading={isFetchingModals}
            dataSource={modalsData?.data || []}
            columns={modalColumns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <span>
          <LinkOutlined /> Mappings & Pricing
        </span>
      ),
      children: (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Type-Model Mapping</h2>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingVtm(null);
                vtmForm.resetFields();
                setVtmModalOpen(true);
              }}
            >
              Add Mapping
            </Button>
          </div>
          <Table
            className="overflow-x-auto"
            loading={isFetchingVtm}
            dataSource={vtmData?.data || []}
            columns={vtmColumns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </div>
      ),
    },
    {
      key: "4",
      label: (
        <span>
          <BgColorsOutlined /> Colors
        </span>
      ),
      children: (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Vehicle Colors</h2>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingColor(null);
                colorForm.resetFields();
                setColorModalOpen(true);
              }}
            >
              Add Color
            </Button>
          </div>
          <Table
            loading={isFetchingColors}
            dataSource={colorsData?.data || []}
            columns={colorColumns}
            rowKey="id"
            className="overflow-x-auto"
            pagination={{ pageSize: 10 }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Vehicle Configurations
        </h1>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        type="card"
        className="bg-white p-4 rounded-xl shadow-sm"
      />

      {/* Type Modal */}
      <Modal
        title={editingType ? "Edit Type" : "Add Type"}
        open={typeModalOpen}
        onCancel={() => setTypeModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={typeForm} layout="vertical" onFinish={handleTypeSubmit}>
          <Form.Item
            label="Type Name"
            name="v_type"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g. Scooter, Bike" />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setTypeModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingType ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Model Modal */}
      <Modal
        title={editingModal ? "Edit Model" : "Add Model"}
        open={modalModalOpen}
        onCancel={() => setModalModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={modalForm} layout="vertical" onFinish={handleModalSubmit}>
          <Form.Item
            label="Model Name"
            name="v_modal"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g. PCX, NMAX" />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setModalModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingModal ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Color Modal */}
      <Modal
        title={editingColor ? "Edit Color" : "Add Color"}
        open={colorModalOpen}
        onCancel={() => setColorModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={colorForm} layout="vertical" onFinish={handleColorSubmit}>
          <Form.Item
            label="Color Name"
            name="color"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g. Red, Matte Black" />
          </Form.Item>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setColorModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingColor ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* VTM Modal */}
      <Modal
        title={editingVtm ? "Edit Mapping" : "Add Mapping"}
        open={vtmModalOpen}
        onCancel={() => setVtmModalOpen(false)}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form form={vtmForm} layout="vertical" onFinish={handleVtmSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="Type"
              name="vehicle_type_id"
              rules={[{ required: true, message: "Required" }]}
            >
              <Select placeholder="Select Type">
                {typesData?.data?.map((t: any) => (
                  <Option key={t.id} value={t.id}>
                    {t.v_type}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Model"
              name="vehicle_modal_id"
              rules={[{ required: true, message: "Required" }]}
            >
              <Select placeholder="Select Model">
                {modalsData?.data?.map((m: any) => (
                  <Option key={m.id} value={m.id}>
                    {m.v_modal}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Basic Price"
              name="price"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                className="w-full"
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
            <Form.Item
              label="Registration Cost (lease)"
              name="registration_cost_lease"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                className="w-full"
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
            <Form.Item
              label="Registration Cost (cash)"
              name="registration_cost_cash"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                className="w-full"
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
            <Form.Item
              label="Insurance Cost"
              name="insurance_cost"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                className="w-full"
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
            <Form.Item
              label="Transport Charges"
              name="transport_charges"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                className="w-full"
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
            <Form.Item
              label="Special Number Level 1"
              name="special_number_level_1"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                className="w-full"
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
            <Form.Item
              label="Special Number Level 2"
              name="special_number_level_2"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                className="w-full"
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
            <Form.Item
              label="Special Number Level 3"
              name="special_number_level_3"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                className="w-full"
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
            <Form.Item
              label="Special Number Level 4"
              name="special_number_level_4"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                className="w-full"
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
            <Form.Item
              label="Special Number Level 5"
              name="special_number_level_5"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                className="w-full"
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
            <Form.Item
              label="Rajya Pranama"
              name="rajya_pranama"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                className="w-full"
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
            <Form.Item
              label="Age Stock"
              name="age_stock"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber
                className="w-full"
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
            <Form.Item
              label="Discount"
              name="discount"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber className="w-full" prefix="Rs." min={0} />
            </Form.Item>
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button onClick={() => setVtmModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingVtm ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
